import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './UpdatesPanel.css';

/* ------------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------------ */

function formatBytes(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// The add-in only accepts Major.Minor.Patch versions.
const VERSION_RE = /^\d+\.\d+\.\d+$/;

/** Suggest the next patch version as x.x.x (e.g. "1.2.3" or legacy "1.2.3.0" -> "1.2.4"). */
function suggestNextVersion(current) {
  if (!current) return '1.0.0';
  const parts = String(current).split('.').map((p) => parseInt(p, 10));
  if (parts.some(Number.isNaN)) return '';
  const [major = 0, minor = 0, patch = 0] = parts;
  return `${major}.${minor}.${patch + 1}`;
}

/** Compute the SHA-256 of a File in the browser, as a lowercase hex string. */
async function sha256OfFile(file) {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Build the manifest the server would auto-generate, for previewing/editing. */
function buildManifestPreview({ version, baseUrl, sha256, hasNotes }) {
  const manifest = {
    latestVersion: version,
    downloadUrl: `${baseUrl}/updates/TreeWorks-${version}.esriAddinX`,
    sha256,
  };
  if (hasNotes) {
    manifest.releaseNotesUrl = `${baseUrl}/updates/v${version.replace(/\./g, '-')}.html`;
  }
  return manifest;
}

/** Truncated SHA-256 for display, with copy button. */
function ShaChip({ sha256, onCopy }) {
  if (!sha256) return null;
  const short = `${sha256.slice(0, 8)}…${sha256.slice(-8)}`;
  return (
    <button
      type="button"
      className="sha-chip"
      title={`SHA-256: ${sha256} (click to copy)`}
      onClick={() => {
        navigator.clipboard.writeText(sha256).then(
          () => onCopy && onCopy('success', 'SHA-256 copied'),
          () => onCopy && onCopy('error', 'Failed to copy')
        );
      }}
    >
      {short}
    </button>
  );
}

/* ------------------------------------------------------------------
 * Drag-drop file picker
 * ------------------------------------------------------------------ */

function DropZone({ id, label, hint, accept, file, onFile, disabled }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);

  const onDrop = (e) => {
    e.preventDefault();
    setOver(false);
    if (disabled) return;
    const dropped = e.dataTransfer?.files?.[0];
    if (dropped) onFile(dropped);
  };

  return (
    <div
      className={`drop-zone${over ? ' over' : ''}${file ? ' has-file' : ''}${disabled ? ' disabled' : ''}`}
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      onClick={() => !disabled && inputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !disabled) inputRef.current?.click(); }}
    >
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={(e) => {
          const picked = e.target.files?.[0];
          if (picked) onFile(picked);
          e.target.value = '';
        }}
        disabled={disabled}
      />
      {file ? (
        <div className="drop-file">
          <div className="drop-file-name">{file.name}</div>
          <div className="drop-file-meta">{formatBytes(file.size)}</div>
          <button
            type="button"
            className="drop-file-clear"
            onClick={(e) => { e.stopPropagation(); onFile(null); }}
            disabled={disabled}
          >
            Remove
          </button>
        </div>
      ) : (
        <div className="drop-empty">
          <div className="drop-label">{label}</div>
          {hint && <div className="drop-hint">{hint}</div>}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------
 * Channel summary card
 * ------------------------------------------------------------------ */

function ChannelCard({ name, channel, info, onRollback, onCopy }) {
  if (!info) {
    return (
      <div className={`channel-card channel-${channel}`}>
        <div className="channel-card-header">
          <h3>{name}</h3>
          <span className="channel-tag">{channel}</span>
        </div>
        <div className="channel-empty">No manifest published yet.</div>
      </div>
    );
  }

  if (info.error) {
    return (
      <div className={`channel-card channel-${channel} channel-error`}>
        <div className="channel-card-header">
          <h3>{name}</h3>
          <span className="channel-tag">{channel}</span>
        </div>
        <div className="channel-empty">Error reading manifest: {info.error}</div>
      </div>
    );
  }

  const m = info.manifest || {};
  const valid = !info.problems || info.problems.length === 0;

  return (
    <div className={`channel-card channel-${channel}`}>
      <div className="channel-card-header">
        <h3>{name}</h3>
        <span className="channel-tag">{channel}</span>
      </div>

      <div className="channel-version-row">
        <div className="channel-version">{m.latestVersion || '—'}</div>
        {valid ? (
          <span className="badge badge-valid">Valid</span>
        ) : (
          <span className="badge badge-invalid" title={(info.problems || []).join('\n')}>Invalid</span>
        )}
        {!m.downloadUrl && (
          <span className="badge badge-warning" title="Add-in will show 'update available' but cannot auto-install">
            Notes-only
          </span>
        )}
      </div>

      <dl className="channel-fields">
        {m.downloadUrl && (
          <>
            <dt>Download</dt>
            <dd>
              <a href={m.downloadUrl} target="_blank" rel="noreferrer">{m.downloadUrl}</a>
            </dd>
          </>
        )}
        {m.sha256 && (
          <>
            <dt>SHA-256</dt>
            <dd><ShaChip sha256={m.sha256} onCopy={onCopy} /></dd>
          </>
        )}
        {m.releaseNotesUrl && (
          <>
            <dt>Release notes</dt>
            <dd><a href={m.releaseNotesUrl} target="_blank" rel="noreferrer">{m.releaseNotesUrl}</a></dd>
          </>
        )}
        <dt>Last published</dt>
        <dd>{formatDate(info.modified)}</dd>
      </dl>

      {!valid && (
        <ul className="channel-problems">
          {info.problems.map((p, i) => <li key={i}>{p}</li>)}
        </ul>
      )}

      <div className="channel-actions">
        <button
          type="button"
          className="btn btn-secondary btn-small"
          onClick={() => onRollback(channel)}
          disabled={!info.canRollback}
          title={info.canRollback ? 'Restore previous manifest' : 'No previous manifest available'}
        >
          Roll back
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------
 * Main panel
 * ------------------------------------------------------------------ */

function UpdatesPanel({ authToken, onAuthExpired, showAlert, showConfirm }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [binaryFile, setBinaryFile] = useState(null);
  const [notesFile, setNotesFile] = useState(null);
  const [version, setVersion] = useState('');
  const [channel, setChannel] = useState('stable');
  const [publishing, setPublishing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [editManifest, setEditManifest] = useState(false);
  const [manifestText, setManifestText] = useState('');
  const [generatingManifest, setGeneratingManifest] = useState(false);

  const authHeader = useMemo(
    () => (authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    [authToken]
  );

  const fetchStatus = useCallback(async () => {
    if (!authToken) return;
    try {
      setLoading(true);
      const res = await fetch('/api/updates/status', { headers: authHeader });
      if (res.status === 401 || res.status === 403) {
        showAlert('error', 'Session expired. Please login again.', onAuthExpired);
        return;
      }
      const data = await res.json();
      if (!res.ok) {
        showAlert('error', data.error || 'Failed to load updates status');
        return;
      }
      setStatus(data);
    } catch (err) {
      console.error('updates status:', err);
      showAlert('error', 'Failed to load updates status');
    } finally {
      setLoading(false);
    }
  }, [authToken, authHeader, onAuthExpired, showAlert]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Suggest the next version whenever the form opens / channel changes.
  useEffect(() => {
    if (!showForm || version) return;
    const current = channel === 'beta'
      ? status?.beta?.manifest?.latestVersion
      : status?.stable?.manifest?.latestVersion;
    setVersion(suggestNextVersion(current));
  }, [showForm, channel, status, version]);

  const resetForm = () => {
    setBinaryFile(null);
    setNotesFile(null);
    setVersion('');
    setChannel('stable');
    setProgress(0);
    setEditManifest(false);
    setManifestText('');
    setGeneratingManifest(false);
  };

  const handleBinary = (file) => {
    if (file && status?.maxBinaryBytes && file.size > status.maxBinaryBytes) {
      showAlert(
        'error',
        `Binary is ${formatBytes(file.size)}, exceeds ${formatBytes(status.maxBinaryBytes)} limit.`
      );
      return;
    }
    setBinaryFile(file);
    // Auto-extract version from a TreeWorks-1.2.3.esriAddinX filename.
    if (file && !version) {
      const m = /TreeWorks-(\d+\.\d+\.\d+)\.esriAddinX$/i.exec(file.name);
      if (m) setVersion(m[1]);
    }
  };

  // Compute the checksum client-side and pre-fill the editable manifest JSON so
  // the admin can review/tweak exactly what gets written before publishing.
  const generateManifest = useCallback(async () => {
    if (!binaryFile) { showAlert('error', 'Pick an .esriAddinX file first.'); return; }
    if (!VERSION_RE.test(version)) {
      showAlert('error', 'Set a valid x.x.x version (e.g. 1.2.3) before generating the manifest.');
      return;
    }
    setGeneratingManifest(true);
    try {
      const sha256 = await sha256OfFile(binaryFile);
      const baseUrl = (status?.baseUrl || 'https://www.treeauth.com').replace(/\/+$/, '');
      const manifest = buildManifestPreview({
        version,
        baseUrl,
        sha256,
        hasNotes: !!notesFile,
      });
      setManifestText(JSON.stringify(manifest, null, 2));
    } catch (err) {
      console.error('generate manifest:', err);
      showAlert('error', 'Failed to compute the binary checksum.');
    } finally {
      setGeneratingManifest(false);
    }
  }, [binaryFile, version, notesFile, status, showAlert]);

  const toggleEditManifest = async (e) => {
    const on = e.target.checked;
    setEditManifest(on);
    if (on && !manifestText && binaryFile && VERSION_RE.test(version)) {
      await generateManifest();
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!binaryFile) { showAlert('error', 'Pick an .esriAddinX file first.'); return; }
    if (!VERSION_RE.test(version)) {
      showAlert('error', 'Version must be Major.Minor.Patch, e.g. 1.2.3.');
      return;
    }

    if (editManifest) {
      if (!manifestText.trim()) {
        showAlert('error', 'The manifest editor is empty. Generate or write a manifest first.');
        return;
      }
      try {
        JSON.parse(manifestText);
      } catch (err) {
        showAlert('error', `Edited manifest is not valid JSON: ${err.message}`);
        return;
      }
    }

    const note = editManifest
      ? ' Your hand-edited manifest will be written as-is (after validation).'
      : '';
    showConfirm(
      `Publish TreeWorks ${version} to the ${channel} channel?${note} Existing ${channel} customers on older versions will be offered this update on their next check.`,
      () => doPublish()
    );
  };

  const doPublish = () => {
    const form = new FormData();
    form.append('binary', binaryFile);
    if (notesFile) form.append('notes', notesFile);
    form.append('version', version);
    form.append('channel', channel);
    if (editManifest && manifestText.trim()) form.append('manifest', manifestText);

    // Use XHR rather than fetch so we get upload progress.
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/updates/publish');
    if (authToken) xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);

    xhr.upload.onprogress = (evt) => {
      if (evt.lengthComputable) setProgress(Math.round((evt.loaded / evt.total) * 100));
    };

    xhr.onload = () => {
      setPublishing(false);
      setProgress(0);
      let data = {};
      try { data = JSON.parse(xhr.responseText); } catch { /* ignore */ }

      if (xhr.status === 401 || xhr.status === 403) {
        showAlert('error', 'Session expired. Please login again.', onAuthExpired);
        return;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        showAlert('success', `Published TreeWorks ${data.manifest?.latestVersion} to ${data.channel}.`);
        setShowForm(false);
        resetForm();
        fetchStatus();
      } else {
        const detail = Array.isArray(data.details) ? `\n${data.details.join('\n')}` : '';
        showAlert('error', `${data.error || 'Publish failed'}${detail}`);
      }
    };

    xhr.onerror = () => {
      setPublishing(false);
      setProgress(0);
      showAlert('error', 'Network error while publishing.');
    };

    setPublishing(true);
    setProgress(0);
    xhr.send(form);
  };

  const rollback = (ch) => {
    showConfirm(`Restore previous ${ch} manifest?`, async () => {
      try {
        const res = await fetch('/api/updates/rollback', {
          method: 'POST',
          headers: { ...authHeader, 'Content-Type': 'application/json' },
          body: JSON.stringify({ channel: ch }),
        });
        const data = await res.json();
        if (!res.ok) { showAlert('error', data.error || 'Rollback failed'); return; }
        showAlert('success', `Rolled back ${ch} manifest.`);
        fetchStatus();
      } catch (err) {
        console.error('rollback:', err);
        showAlert('error', 'Rollback failed');
      }
    });
  };

  const deleteBinary = (filename) => {
    showConfirm(`Permanently delete ${filename} from the server?`, async () => {
      try {
        const res = await fetch(`/api/updates/binary/${encodeURIComponent(filename)}`, {
          method: 'DELETE',
          headers: authHeader,
        });
        const data = await res.json();
        if (!res.ok) { showAlert('error', data.error || 'Delete failed'); return; }
        showAlert('success', `Deleted ${filename}`);
        fetchStatus();
      } catch (err) {
        console.error('delete binary:', err);
        showAlert('error', 'Delete failed');
      }
    });
  };

  const onCopy = (type, msg) => showAlert(type, msg);

  const referencedBinaries = new Set();
  for (const ch of ['stable', 'beta']) {
    const url = status?.[ch]?.manifest?.downloadUrl;
    if (typeof url === 'string') {
      const last = url.split('/').pop();
      if (last) referencedBinaries.add(last);
    }
  }

  return (
    <div className="updates-panel">
      <div className="updates-header">
        <div>
          <h2>Add-in updates</h2>
          <p>
            Publish a new <code>.esriAddinX</code> to the TreeWorks add-in. Customers
            check <code>/updates/manifest.json</code> once per day and are offered
            the new version automatically.
          </p>
        </div>
        <div className="updates-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => { setShowForm((v) => !v); if (showForm) resetForm(); }}
          >
            {showForm ? 'Cancel' : '+ Publish update'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={fetchStatus}>
            Refresh
          </button>
        </div>
      </div>

      {showForm && (
        <form className="publish-form" onSubmit={submit}>
          <div className="publish-form-grid">
            <DropZone
              id="binary-drop"
              label="Drop the .esriAddinX here"
              hint="or click to choose a file"
              accept=".esriAddinX"
              file={binaryFile}
              onFile={handleBinary}
              disabled={publishing}
            />
            <DropZone
              id="notes-drop"
              label="Drop release notes (optional)"
              hint=".html — opens in customer's browser"
              accept=".html,.htm"
              file={notesFile}
              onFile={setNotesFile}
              disabled={publishing}
            />
          </div>

          <div className="publish-form-row">
            <div className="form-group">
              <label htmlFor="publish-version">Version</label>
              <input
                id="publish-version"
                type="text"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder="1.0.2"
                disabled={publishing}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="publish-channel">Channel</label>
              <select
                id="publish-channel"
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                disabled={publishing}
              >
                <option value="stable">Stable (all customers)</option>
                <option value="beta">Beta (opt-in keys only)</option>
              </select>
            </div>
          </div>

          <div className="manifest-edit-toggle">
            <label className="manifest-edit-check">
              <input
                type="checkbox"
                checked={editManifest}
                onChange={toggleEditManifest}
                disabled={publishing}
              />
              <span>Review &amp; edit the manifest before publishing</span>
            </label>
          </div>

          {editManifest && (
            <div className="manifest-editor">
              <div className="manifest-editor-header">
                <label htmlFor="manifest-json">Manifest JSON</label>
                <button
                  type="button"
                  className="btn btn-secondary btn-small"
                  onClick={generateManifest}
                  disabled={publishing || generatingManifest || !binaryFile}
                  title="Recompute the checksum and regenerate the manifest from the form above"
                >
                  {generatingManifest ? 'Generating…' : 'Generate from form'}
                </button>
              </div>
              <textarea
                id="manifest-json"
                className="manifest-textarea"
                value={manifestText}
                onChange={(e) => setManifestText(e.target.value)}
                placeholder='Click "Generate from form" to compute the checksum and pre-fill this, or paste your own manifest JSON.'
                spellCheck={false}
                rows={12}
                disabled={publishing}
              />
              <p className="manifest-editor-hint">
                Written verbatim after server-side validation. The binary and notes
                are still saved using the <strong>Version</strong> above, so keep
                <code> downloadUrl</code> and <code>sha256</code> consistent with the
                uploaded file unless you know what you're doing.
              </p>
            </div>
          )}

          {publishing && (
            <div className="publish-progress" aria-label="Upload progress">
              <div className="publish-progress-bar" style={{ width: `${progress}%` }} />
              <span className="publish-progress-label">Uploading… {progress}%</span>
            </div>
          )}

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={publishing || !binaryFile}>
              {publishing ? 'Publishing…' : 'Publish'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => { setShowForm(false); resetForm(); }}
              disabled={publishing}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="loading">Loading update status…</div>
      ) : status ? (
        <>
          <div className="channel-grid">
            <ChannelCard name="Stable" channel="stable" info={status.stable} onRollback={rollback} onCopy={onCopy} />
            <ChannelCard name="Beta"   channel="beta"   info={status.beta}   onRollback={rollback} onCopy={onCopy} />
          </div>

          <h3 className="binaries-heading">Published binaries</h3>
          {status.binaries.length === 0 ? (
            <div className="empty-state"><p>No binaries on disk yet.</p></div>
          ) : (
            <div className="table-container">
              <table className="binaries-table">
                <thead>
                  <tr>
                    <th>Filename</th>
                    <th>Size</th>
                    <th>Modified</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {status.binaries.map((b) => {
                    const inUse = referencedBinaries.has(b.filename);
                    return (
                      <tr key={b.filename}>
                        <td className="binary-name">
                          <a href={b.url} target="_blank" rel="noreferrer">{b.filename}</a>
                        </td>
                        <td>{formatBytes(b.sizeBytes)}</td>
                        <td>{formatDate(b.modified)}</td>
                        <td>
                          {inUse
                            ? <span className="badge badge-valid">In use</span>
                            : <span className="badge badge-muted">Old</span>}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="action-btn delete-btn"
                            onClick={() => deleteBinary(b.filename)}
                            disabled={inUse}
                            title={inUse ? 'Referenced by a current manifest' : 'Delete file'}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <div className="empty-state"><p>Could not load update status.</p></div>
      )}
    </div>
  );
}

export default UpdatesPanel;
